import { useCallback, useEffect, useMemo } from "react";
import { useDebounce } from "@onshopfront/components";
import { InMemorySearch } from "../../../../../app/Utilities/Platform/Search/InMemorySearch";
import type { NavigationType } from "../Navigation";

export interface SearchResult {
    parents: Array<string>;
    name: string;
}

export interface NavigationLayer {
    parents: Array<string>;
    name: string | null;
    layer: number | NavigationType;
}

export type NavigationWithSearch = NavigationType & {
    _search?: Record<string, Record<string, { link: string; parent: string; }>>;
};

function buildResultsForNavigation(navigation: NavigationWithSearch): Array<SearchResult> {
    const results: Array<SearchResult> = [];

    // This will process the navigation as a LIFO stack
    let current: NavigationLayer;
    const pending: Array<NavigationLayer> = [{
        parents: [],
        name   : null,
        layer  : navigation,
    }];

    while(pending.length) {
        current = pending.pop() as NavigationLayer;

        if(typeof current.layer === "number") {
            if(typeof current.name === "string") {
                results.push({
                    parents: current.parents,
                    name   : current.name,
                });
            }

            continue;
        }

        if("_link" in current.layer && typeof current.layer._link === "number" && typeof current.name === "string") {
            results.push({
                parents: current.parents,
                name   : current.name,
            });
        }

        const keys = Object.keys(current.layer);

        const parents: Array<string> = [ ...current.parents ];

        if(typeof current.name === "string") {
            parents.push(current.name);
        }

        // Process the keys backwards so the last item added to the pending stack will be alphabetically first
        for(let i = keys.length - 1; i >= 0; i--) {
            if(keys[i].startsWith("_")) {
                continue;
            }

            pending.push({
                parents,
                name : keys[i],
                layer: current.layer[keys[i]],
            });
        }
    }

    if(navigation._search) {
        for(const topLevel in navigation._search) {
            if(!Object.hasOwn(navigation._search, topLevel)) {
                continue;
            }

            for(const item in navigation._search[topLevel]) {
                if(!Object.hasOwn(navigation._search[topLevel], item)) {
                    continue;
                }

                results.push({
                    parents: [ topLevel, navigation._search[topLevel][item].parent ],
                    name   : item,
                });
            }
        }
    }

    return results;
}

/**
 * Hook for handling documentation navigation search
 */
export function useNavigationSearch(navigation: NavigationWithSearch): [
    (value: string) => void,
    Array<SearchResult>,
    Array<SearchResult>,
] {
    const indexMap = useMemo(() => {
        return new InMemorySearch<number>();
    }, []);

    const rawResults = useMemo(() => {
        return buildResultsForNavigation(navigation);
    }, [ navigation ]);

    useEffect(() => {
        indexMap.empty();

        for(let i = 0, l = rawResults.length; i < l; i++) {
            indexMap.add(i, rawResults[i].name);

            for(const parent of rawResults[i].parents) {
                indexMap.add(i, parent);
            }
        }
    }, [ indexMap, rawResults ]);

    const onSearch = useCallback(async (search: string): Promise<Array<SearchResult> | undefined> => {
        const needle = search.trim();

        if(!needle) {
            return undefined;
        }

        const indices = await indexMap.search(needle);

        const results: Array<SearchResult> = [];

        for(const index of indices) {
            results.push(rawResults[index]);
        }

        return results;
    }, [ rawResults, indexMap ]);

    const [ searchCallback, searchResults ] = useDebounce(onSearch, 100);

    const results = useMemo(() => {
        if(typeof searchResults === "undefined") {
            return rawResults;
        }

        return searchResults;
    }, [ searchResults, rawResults ]);

    return [
        searchCallback,
        results,
        rawResults,
    ];
}
